import { CustomSettingsInterface } from 'client/Interfaces/CustomSettingsInterface'
import { TaxRateInterface } from 'client/Interfaces/ExternalVATRatesInterface'
import dayjs, { Dayjs } from 'dayjs'
import moment from 'moment'

import { CompanyInterface } from '../../Interfaces/CompanyInterface'
import { CustomerInterface } from '../../Interfaces/CustomerInterface'
import {
  DocumentType,
  DocumentTypes,
  DraftInterface,
  InvoicerInterface,
  InvoiceTypes,
} from '../../Interfaces/Draft/DraftInterface'
import { DraftFieldsInterface, FieldsInterface } from '../../Interfaces/Draft/FieldsInterface'
import { defFields, LineInterface } from '../../Interfaces/Draft/LineInterface'
import { InvoiceTypesInterface } from '../../Interfaces/InvoiceTypeInterface'
import { ModelInterface } from '../../Interfaces/Settings/ModelInterface'
import { PaymentMeanInterface, PaymentMeans } from '../../Interfaces/Settings/PaymentMeanInterface'
import { SettingsInterface } from '../../Interfaces/Settings/SettingsInterface'
import { StateInterface } from '../../Interfaces/StateInterface'
import { TokenInterface } from '../../Interfaces/TokenInterface'
import { defaultTypeForDocument, getInvoiceType, getLocale, invoiceTypesReplacementMap } from '../../Other/Configs'
import { calculateDaysFromStartDate } from '../../Other/functions/dateFunctions'
import { Immutable } from '../../Other/Immutable'
import { compactObject, flattenObject } from '../../Other/Object'
import { l } from '../../Other/Translation'
import { hasModuleAvailable, moduleNames } from '../../reducers/settings'

const DEFAULT_PAYMENT_TIME = 30
const DEFAULT_INVOICE_TYPE = 1

export const defaultLine = (settings: SettingsInterface, taxRates?: TaxRateInterface[]): LineInterface => {
  const companySettings = settings.values.company
  const { vatRateId: vatRateIdFromSettings, vat } = companySettings
  const vatRateId = vatRateIdFromSettings || ''
  const invoiceModel = settings.models.find((m) => m.type === 'invoice')
  const defaultCurrency = invoiceModel.fields.find((f) => f.name === 'currency')
  let currency = 'EUR'

  if (defaultCurrency) {
    currency = defaultCurrency.default as string
  }

  let productType: string = ''

  if (invoiceModel && invoiceModel.fields && Array.isArray(invoiceModel.fields)) {
    const productTypeSetting = invoiceModel.fields.find((field) => field.name === 'productType')

    productType = ((productTypeSetting && productTypeSetting.default) || '').toString()
  }
  const externalVATPct = taxRates?.find((t) => t?.id === vatRateId)?.rate
  // hopefullly it will be a temporary solution, todo: save external vatPct to settings
  // set vatPct for next users. if vatRateId, get external vatPct
  const vatPct = vatRateId && (externalVATPct || externalVATPct === 0) ? externalVATPct : vat
  return {
    date: new Date().toISOString(),
    type: 'row',
    description: '',
    productType,
    quantity: 1,
    unitPrice: 0,
    vatPct,
    vatRateId,
    taxTotal: 0,
    netTotal: 0,
    grossTotal: 0,
    unit: 'h',
    defField: defFields.PRICE,
    discount: 0,
    totalDiscount: 0,
    currency,
  }
}

export const isValidPaymentMean = (pm: PaymentMeanInterface): boolean => {
  const { fields } = pm
  if (!fields) {
    return false
  }
  const valid = Object.keys(fields).some((f): boolean => {
    return typeof fields[f] !== 'undefined' && fields[f].length > 0
  })
  return valid
}

export const getDefaultLocale = (state: StateInterface, documentType: DocumentType): string => {
  const model = getModel(state.settings, documentType)
  const language = model.fields.filter((f) => {
    return f.name === 'language'
  })[0].default as string
  return getLocale(state.config, language, state.token.country)
}

export const isFullscopeCountry = (country: string): boolean => ['FI', 'SE', 'AX'].includes(country)

export const extractFieldsFromSettings = (
  settings: SettingsInterface,
  { country }: TokenInterface,
  documentType: DocumentType,
  invoiceTypes: InvoiceTypesInterface,
) => {
  const model = getModel(settings, documentType)
  const fields: { [key: string]: any } = {}
  model.fields.forEach((field) => {
    fields[field.name] = {
      visible: field.visible,
      name: field.name,
    }
    if (typeof field.default !== 'undefined') {
      switch (field.name) {
        case 'type':
          if (
            field.default &&
            invoiceTypes[field.default] &&
            Object.keys(invoiceTypesReplacementMap).includes(invoiceTypes[field.default].type)
          ) {
            const countryCode = invoiceTypes[field.default].countryCode
            const type = invoiceTypesReplacementMap[invoiceTypes[Number(field.default)].type]
            const found = Object.entries(invoiceTypes).find(
              ([_key, value]) => value.type === type && value.countryCode === countryCode,
            )
            if (found) {
              fields[field.name].value = found[1].id
              break
            }
          }

          fields[field.name].value = field.default ? parseInt(String(field.default), 10) : DEFAULT_INVOICE_TYPE
          break
        case 'paymentTime':
          fields[field.name].value = field.default ? parseInt(String(field.default), 10) : DEFAULT_PAYMENT_TIME
          break
        case 'interest':
          fields[field.name].value = parseFloat(String(field.default))
          break
        case 'discountEnabled':
          fields[field.name].value = field.default === 'true'
          break
        case 'template':
          // TODO CORE-3281 after nordic migration make the OneP template the default
          fields[field.name].value = isFullscopeCountry(country) ? `${field.default}OneP` : field.default
          break
        case 'paymentScheduleEnabled':
          fields[field.name].value = field.default === 'true'
          break
        default:
          fields[field.name].value = field.default
      }

      if (field.name === 'referenceNumber' && !/^[0-9]{1,32}$/.test(String(field.default))) {
        fields[field.name].value = ''
      }

      if (field.name === 'recipientReference') {
        // ignore the value coming from settings
        fields[field.name].value = ''
      }
    }
  })
  return fields
}

export const getDefaultCustomer = ({ customers }: StateInterface): CustomerInterface | null => {
  const hasOneCustomer = customers && customers.collection && customers.collection.length === 1
  return hasOneCustomer ? Immutable.copy(customers.collection[0]) : null
}

export const getPaymentMeans = (settings: SettingsInterface) => {
  const paymentMeans: any = Immutable.copy(settings.values.paymentMethods)
    .filter(isValidPaymentMean)
    .map((pm) => {
      const { type, fields } = pm
      return { type, fields }
    })
  for (const method of paymentMeans) {
    delete method._id
  }
  return paymentMeans
}

export const getModel = (settings: SettingsInterface, document: DocumentType): ModelInterface => {
  return settings.models.filter((model) => {
    return model.type === document
  })[0]
}

export const getTradeName = (settings: SettingsInterface, documentType: DocumentType) => {
  const model = getModel(settings, documentType)
  // always first check the default === true, otherwise take the sender one
  // if it still failes take the first one as default
  const tradeNames = settings.values.company.tradeNames
  const senderField = model.fields.filter((f) => {
    return f.name === 'sender'
  })[0]
  const sender = senderField && senderField.default
  let tradeName
  let senderName
  if (tradeNames && tradeNames.length > 1) {
    tradeNames.forEach((name) => {
      if (name.default === true) {
        tradeName = name
      }
      if (name._id === sender) {
        senderName = name
      }
    })

    if (!tradeName && senderName) {
      tradeName = senderName
    }

    if (!tradeName) {
      tradeName = tradeNames[0]
    }
  } else {
    tradeName = tradeNames[0]
  }
  return tradeName
}

export const makeMandatoryFieldsVisible = (
  fields: DraftFieldsInterface,
  settings: SettingsInterface,
  document: DocumentType,
): DraftFieldsInterface => {
  const model = getModel(settings, document)
  const schema = settings.schemas[model.type]
  // Make all mandatory fields always visible
  const excludeFields = ['discount']
  schema.fields.forEach((field) => {
    if (excludeFields.indexOf(field.name) >= 0) {
      return
    }
    if (!fields[field.name]) {
      fields[field.name] = {
        name: field.name,
      }
    }
    if (field.mandatory) {
      fields[field.name].visible = true
    }
  })

  return Immutable.copy(fields)
}

export const getFields = (
  document: DocumentType,
  settings: SettingsInterface,
  token: TokenInterface,
  company: CompanyInterface,
  state: StateInterface,
): DraftFieldsInterface => {
  const fields = extractFieldsFromSettings(settings, token, document, state.config.invoiceTypes)
  const { country } = token
  const now = dayjs()
  let then = dayjs()
  const defaultTemplateName = isFullscopeCountry(country) ? 'defaultOneP' : 'default'
  const templateName = (fields.template && fields.template.value) || defaultTemplateName
  for (const name in fields) {
    const field = fields[name]
    switch (name) {
      case 'paymentTime':
        then = calculateDaysFromStartDate(now, parseInt(field.value, 10))
        break
      case 'columns':
        field.value = field.value.split(',')
        break
    }
  }
  let updatedFields: DraftFieldsInterface = {
    type: {
      name: 'type',
      value: getInvoiceType(defaultTypeForDocument(document), state.config, company.country).id,
    },
    template: {
      name: 'template',
      value: `${country}.${templateName}`,
    },
    footerText: {
      name: 'footerText',
      value: '',
    },
    // Discount fields
    discountEnabled: {
      name: 'discountEnabled',
      value: false,
    },
    discountLocation: {
      name: 'discountLocation',
      value: 'perTotal',
    },
    discountAmount: {
      name: 'discountAmount',
      value: 0,
    },
    discountType: {
      name: 'discountType',
      value: 'amount',
    },
    discountVAT: {
      name: 'discountVAT',
      value: 'postVAT',
    },
    ...(Immutable.copy(fields) as FieldsInterface),
    // Also add some fields that are missing from settings
    issueDate: {
      name: 'issueDate',
      value: now.toISOString(),
    },
    locale: {
      name: 'locale',
      value: getDefaultLocale(state, document),
    },
    timeZone: {
      name: 'timeZone',
      visible: true,
      value: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    deduction: {
      name: 'deduction',
      value: 0,
      visible: false,
    },
    recurringProfileEnabled: {
      name: 'recurringProfileEnabled',
      visible: false,
      value: false,
    },
  }

  // apply deduction value if InvoiceType is either SALES_HOUSEHOLD_SE_ROT or SALES_HOUSEHOLD_SE_RUT
  const invoiceTypeId = Number(updatedFields.type.value)
  if ([InvoiceTypes.SALES_HOUSEHOLD_SE_ROT, InvoiceTypes.SALES_HOUSEHOLD_SE_RUT].includes(invoiceTypeId)) {
    updatedFields.deduction.visible = true
    if (invoiceTypeId === InvoiceTypes.SALES_HOUSEHOLD_SE_ROT) {
      updatedFields.deduction.value = 30
    }
    if (invoiceTypeId === InvoiceTypes.SALES_HOUSEHOLD_SE_RUT) {
      updatedFields.deduction.value = 50
    }
  }

  if (updatedFields.dueDate) {
    updatedFields.dueDate.value = then.toISOString()
  }
  const isPaymentTime = updatedFields.paymentTime && updatedFields.paymentTime.visible
  const isDueDate = updatedFields.dueDate && updatedFields.dueDate.visible
  const defaultDueDate = calculateDaysFromStartDate(now, DEFAULT_PAYMENT_TIME).toISOString()
  if (isDueDate && !updatedFields.dueDate.value) {
    fields.dueDate.value = defaultDueDate
  }
  if (!isPaymentTime && !isDueDate && document !== DocumentTypes.ESTIMATE) {
    // make due date visible and set default value
    if (!updatedFields.dueDate) {
      updatedFields.dueDate = {
        name: 'dueDate',
        visible: true,
        value: defaultDueDate,
      }
    } else {
      updatedFields.dueDate.visible = true
      updatedFields.dueDate.value = defaultDueDate
    }
  }
  updatedFields = makeMandatoryFieldsVisible(updatedFields, settings, document)
  if (updatedFields.template.value.indexOf('.') === -1) {
    updatedFields.template.value = country + '.' + updatedFields.template.value
  }
  updatedFields.recurringProfileEnabled.visible = hasModuleAvailable(state.settings, moduleNames.recurringProfiles)
  return updatedFields
}

export const getSummary = (state: StateInterface) => {
  return {
    currency: state.config.currencies[state.company.currencyId].shortName,
    grossTotal: 0,
    netTotal: 0,
    taxTotal: 0,
    workTotal: 0,
    reductionTotal: 0,
    discountTotal: 0,
    taxSummary: [],
  }
}

export const getStyles = (
  settings: SettingsInterface,
  { country }: TokenInterface,
  fields: DraftFieldsInterface,
  document: DocumentType,
) => {
  const defaultTemplateName = isFullscopeCountry(country) ? 'defaultOneP' : 'default'
  const defaultStyles =
    (settings.styles[document] && settings.styles[document][defaultTemplateName]) ||
    settings.styles.invoice[defaultTemplateName]

  if (fields.template && settings.styles[document] && settings.styles[document][fields.template.value.substr(3)]) {
    return settings.styles[document][fields.template.value.substr(3)]
  } else {
    return defaultStyles
  }
}

export const getInvoicer = (
  settings: SettingsInterface,
  company: CompanyInterface,
  { country }: TokenInterface,
  documentType: DocumentType,
): InvoicerInterface => {
  const tradeName = getTradeName(settings, documentType)
  const {
    rcs = '',
    legalStatus = '',
    firmenbuchregister = false,
    hrNr,
    amtsgericht,
    isNotVATLiable = false,
    taxPrepaymentRegistered,
  } = settings.values.company
  const firmProps = firmenbuchregister ? { firmenbuchregister } : {}
  const hrNrProp = hrNr ? { hrNr } : {}
  const amtsgerichtProp = amtsgericht ? { amtsgericht } : {}
  const ftaxProp = typeof taxPrepaymentRegistered === 'boolean' ? { taxPrepaymentRegistered } : {}
  const invoicer: InvoicerInterface = {
    tradeName: { ...tradeName, country },
    vatNumber: company.vatNumber,
    businessId: company.businessId,
    isNotVATLiable,
    rcs,
    legalStatus,
    id: company.id,
    ...hrNrProp,
    ...amtsgerichtProp,
    ...ftaxProp,
    ...firmProps,
  }
  return invoicer
}

interface InvoiceStylesInterface {
  [key: string]: { [key: string]: string | number }
}

export const defaultDocument = (
  document = DocumentTypes.INVOICE,
  settings: SettingsInterface,
  token: TokenInterface,
  company: CompanyInterface,
  state: StateInterface,
  customSettings?: CustomSettingsInterface,
): DraftInterface => {
  if (!settings) {
    return null
  }
  const model = getModel(settings, document)
  const fields = getFields(document, settings, token, company, state)
  const styles = getStyles(settings, token, fields, document) as InvoiceStylesInterface
  const paymentMeans = getPaymentMeans(settings)
  const summary = getSummary(state)
  const invoicer = getInvoicer(settings, company, token, document)
  const defaultCharacterLimit = { description: false, textFields: false }
  const externalVATRates = state?.externalVATRates?.taxRates
  const invoice: DraftInterface = {
    id: 0,
    invoicer,
    receiver: null,
    fields,
    summary,
    styles,
    paymentMeans,
    document: model.type as DocumentType,
    model: model.name,
    lines: [defaultLine(settings, externalVATRates)],
    invalidFields: [],
    isDirty: false,
    customSettings,
    eInvoiceSending: false,
    characterLimitReached: defaultCharacterLimit,
  }
  return invoice
}

export const isInvoiceNumberNewSequence = (draft, sequences) => {
  const { invoiceNumber } = draft
  if (!invoiceNumber || !invoiceNumber.prefix) {
    return false
  }
  return !sequences.all.find((s) => s.prefix === draft.invoiceNumber.prefix)
}

export const hasMinimumInfo = (draft: DraftInterface) => {
  // This is for special purpose of determining if sender info has been filled at all
  // so that the dummy sender info can be shown in the preview
  draft = Immutable.copy(draft)
  // these fields are coming from system, so ignore.
  // having tradeName.name is not enough so ignore that too
  delete draft.invoicer.id
  delete draft.invoicer.tradeName._id
  delete draft.invoicer.tradeName.default
  delete draft.invoicer.tradeName.country
  delete draft.invoicer.tradeName.name
  delete draft.invoicer.taxPrepaymentRegistered
  delete draft.invoicer.isNotVATLiable

  const invoicerInfo: boolean = Object.values(compactObject(flattenObject(draft.invoicer))).some((s) => Boolean(s))
  const paymentInfo = draft.paymentMeans
    ? draft.paymentMeans
        .reduce((acc, cur) => {
          if (!cur.fields) {
            return [...acc]
          }
          return [...acc, ...Object.values(compactObject(flattenObject(cur.fields)))]
        }, [])
        .join('')
    : ''
  return invoicerInfo || paymentInfo
}
